import type { TypedShoppingCart } from "@Resources/js/laravel.d.ts"

import { ZERO_PRICE_DATA } from "@Modules/shared/Data/Price"

import CartItemModel, { CartItem } from "./CartItemModel"

export interface ShoppingCart extends Omit<TypedShoppingCart, "items"> {
  items: CartItem[]
  isEmpty: () => boolean
}

export default function ShoppingCartModel(data: TypedShoppingCart): ShoppingCart {
  if (!data || typeof data !== "object") {
    throw new Error(`ShoppingCartModel: expected TypedShoppingCart object, got ${typeof data}`)
  }
  if (!Array.isArray(data.items)) {
    throw new Error(
      `ShoppingCartModel: expected items array, got ${typeof data.items}. Response keys: ${Object.keys(data).join(", ")}`,
    )
  }
  const items = data.items.map(CartItemModel)

  const that: ShoppingCart = {
    instance: data.instance,
    items,

    get quantity(): number {
      return data.quantity ?? items.reduce((sum, item) => sum + item.quantity, 0)
    },

    get price() {
      if (data.price) return data.price
      return items.reduce(
        (sum, item) => ({
          amount: sum.amount + (item.price?.amount ?? 0),
          currency: item.price?.currency ?? sum.currency,
        }),
        { ...ZERO_PRICE_DATA },
      )
    },

    get discount() {
      if (data.discount) return data.discount
      return items.reduce(
        (sum, item) => ({
          amount: sum.amount + (item.discount?.amount ?? 0),
          currency: item.discount?.currency ?? sum.currency,
        }),
        { ...ZERO_PRICE_DATA },
      )
    },

    get total() {
      if (data.total) return data.total
      return items.reduce(
        (sum, item) => ({
          amount: sum.amount + (item.total?.amount ?? 0),
          currency: item.total?.currency ?? sum.currency,
        }),
        { ...ZERO_PRICE_DATA },
      )
    },

    isEmpty: () => that.quantity === 0,
  }

  return that
}
